import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { Card } from '@/components/ui/card';
import { MapPin, Loader2 } from 'lucide-react';

// Fix für die Leaflet-Icons in Vite/React
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface PropertyMapProps {
  address: string;
  city?: string | null;
  zip?: string | null;
}

// Hilfskomponente um die Karte zu zentrieren, wenn sich die Koordinaten ändern
function RecenterMap({ coords }: { coords: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(coords, 15);
  }, [coords, map]);
  return null;
}

export function PropertyMap({ address, city, zip }: PropertyMapProps) {
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function geocode() {
      if (!address && !city) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const query = [address, zip, city].filter(Boolean).join(', ');
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`
        );
        const data = await response.json();

        if (data && data.length > 0) {
          setCoords([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
        } else {
          setError('Adresse konnte nicht gefunden werden');
        }
      } catch (err) {
        setError('Fehler bei der Kartendarstellung');
      } finally {
        setIsLoading(false);
      }
    }

    geocode();
  }, [address, city, zip]);

  if (!address && !city) return null;

  return (
    <Card className="overflow-hidden border border-border bg-background-subtle h-[300px] relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-sm">
          <Loader2 className="w-6 h-6 animate-spin text-accent-gold" />
        </div>
      )}
      
      {error ? (
        <div className="flex flex-col items-center justify-center h-full text-foreground-muted p-6 text-center">
          <MapPin className="w-8 h-8 mb-2 opacity-20" />
          <p className="text-sm">{error}</p>
        </div>
      ) : coords ? (
        <MapContainer
          center={coords}
          zoom={15}
          scrollWheelZoom={false}
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={coords} />
          <RecenterMap coords={coords} />
        </MapContainer>
      ) : (
        <div className="flex items-center justify-center h-full text-foreground-muted">
          <p className="text-sm">Suche Standort...</p>
        </div>
      )}
    </Card>
  );
}
